import type { AuthedRequester } from "@/lib/brokerAuth";
import {
  bondWireValues,
  invalidFields,
  type BondFieldValue,
  type BondRenderField,
  type BondValueBag,
} from "@/lib/bondFields";
import { visibleBondFields } from "@/lib/bondFieldSections";
import { unansweredBondUnits } from "@/lib/bondFieldPairs";
import { withMissingInline } from "@/lib/bondFieldIssues";
import { readBondRows, type BondRowView } from "@/lib/bondStateModel";
import type { BondFinalField, BondSubmission } from "@/lib/brokerBonds";

// BRK-S419 — Edit Bond Request: a FILED submission's bond details changed
// before award (D1, reversing BRK-S197's "after submit, nothing is
// editable"). The dialog draws the configuration's final-step fields through
// the shared `BondDetailsFields`, prefilled from `values.final`, with PMS's
// `editPolicy.locks` read-only; this module is its pure half and its seam.
//
// THE ROUTE IS PMS-S845's (P10, backed by SUR-S269), pinned at BRK-S511 —
// its answer is a seam surface and `test/lib/bondEditContract.test.ts` holds
// the body to the pinned request. `PUT
// /api/broker/bond/submissions/{id}/edit` with `{values}` (only the changed
// fields), answering the order view: `changeRequest` kind `edit` pending, the
// voided bid bond row and the new history rows. Nothing here decides whether
// an edit is allowed or what it does to the bond — `actions.edit` and the
// response say so (#7).

const SUBMISSIONS = "/api/broker/bond/submissions";

/** The edit's body: each CHANGED final-step field by key, in the contract's
 *  spelling (D-10 — ISO dates, plain numbers, E.164 phones); `null` clears an
 *  answer. PMS-S845. */
export interface BondEditInput {
  values: Record<string, BondFieldValue | null>;
}

/** PUT the changed fields; the 2xx is the order view, which the page shows. */
export function editBondSubmission(
  request: AuthedRequester,
  id: string,
  input: BondEditInput,
): Promise<BondSubmission> {
  return request<BondSubmission>(
    `${SUBMISSIONS}/${encodeURIComponent(id)}/edit`,
    { method: "PUT", body: input },
  );
}

/** The warning over the form when the order carries an issued bid bond —
 *  decision 8's wording: "review", never "underwriting". */
export const EDIT_VOID_WARNING =
  "Saving these changes voids the issued bid bond and sends the request back for review.";

/** The issued bid bond an edit would void (`bonds[]`: kind `bid`, status
 *  `issued`), or null. Read from the served rows, never inferred. */
export function issuedBidBond(order: { bonds?: unknown }): BondRowView | null {
  return (
    readBondRows(order.bonds).find(
      (row) => row.kind === "bid" && row.status === "issued",
    ) ?? null
  );
}

/** The final-step fields the dialog edits. A `files` field (matched by its
 *  declared TYPE) is a document drop, not a bond detail: a filed order's
 *  documents are added on the Submission Documents tab (BRK-S424), so the
 *  dialog leaves it there rather than drawing a drop that cannot attach. */
export function editableFinalFields(
  fields: readonly BondFinalField[],
): BondFinalField[] {
  return fields.filter((f) => f.type !== "files");
}

const ADDRESS_PARTS = [
  "street",
  "addressLine2",
  "city",
  "state",
  "zip",
  "county",
] as const;

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/**
 * The captured bag (`values.final`) narrowed into what the controls take: a
 * scalar as it is, an address as its string parts. Anything else — a null, a
 * list — reads as unanswered rather than being cast into a control (#7).
 */
export function editableBag(
  raw: Readonly<Record<string, unknown>>,
): BondValueBag {
  const bag: BondValueBag = {};
  for (const [key, value] of Object.entries(raw)) {
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      bag[key] = value;
    } else if (isRecord(value)) {
      const parts: Record<string, string> = {};
      for (const part of ADDRESS_PARTS) {
        const v = value[part];
        if (typeof v === "string") parts[part] = v;
      }
      bag[key] = {
        street: parts.street ?? "",
        city: parts.city ?? "",
        state: parts.state ?? "",
        zip: parts.zip ?? "",
        ...(parts.addressLine2 === undefined
          ? {}
          : { addressLine2: parts.addressLine2 }),
        ...(parts.county === undefined ? {} : { county: parts.county }),
      };
    }
  }
  return bag;
}

/** Nothing answered: absent, empty text, or an address with no part typed. */
function isBlank(value: BondFieldValue | undefined): boolean {
  if (value === undefined || value === "") return true;
  if (isRecord(value)) {
    return Object.values(value).every((v) => v === "" || v === undefined);
  }
  return false;
}

/** A value with its keys in a fixed order, so an address rebuilt by the
 *  control in another order is still the same address. */
function canonical(value: BondFieldValue | undefined): string {
  if (isRecord(value)) {
    const entries = Object.entries(value)
      .filter(([, v]) => v !== "" && v !== undefined)
      .sort(([a], [b]) => a.localeCompare(b));
    return JSON.stringify(entries);
  }
  return JSON.stringify(value);
}

/**
 * Only the fields the broker CHANGED, in the contract's spelling. The bag is
 * put through `bondWireValues` first (a phone humanised by its control is the
 * same E.164 number), then compared with what the order held. A cleared
 * answer is sent as `null`; a locked field and a key the configuration does
 * not declare are never sent.
 */
export function changedBondValues(
  fields: readonly BondRenderField[],
  before: BondValueBag,
  after: BondValueBag,
  locks: Readonly<Record<string, string>> = {},
): Record<string, BondFieldValue | null> {
  const wire = bondWireValues(fields, after);
  const changed: Record<string, BondFieldValue | null> = {};
  for (const { name } of fields) {
    if (Object.hasOwn(locks, name)) continue;
    const was = before[name];
    const now = wire[name];
    if (isBlank(was) && isBlank(now)) continue;
    if (isBlank(now)) {
      changed[name] = null;
      continue;
    }
    if (
      now !== undefined &&
      (isBlank(was) || canonical(was) !== canonical(now))
    ) {
      changed[name] = now;
    }
  }
  return changed;
}

/**
 * The wizard's own checks over the fields a broker can change now — visible
 * and not locked: field → why it is not an answer (a required field left
 * blank, a value its type rejects). Empty when the form is ready to send.
 * Shared by Edit Bond Request and the rider dialog (BRK-S420), so both refuse
 * exactly what the wizard's project & bond terms step refuses.
 */
export function bondEditIssues(
  fields: readonly BondRenderField[],
  values: BondValueBag,
  locks: Readonly<Record<string, string>>,
): Record<string, string> {
  const editable = visibleBondFields(fields, values).filter(
    (f) => !Object.hasOwn(locks, f.name),
  );
  return withMissingInline(
    Object.fromEntries(
      invalidFields(editable, values).map((b) => [b.field.name, b.reason]),
    ),
    unansweredBondUnits(editable, values),
  );
}
